import { FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Download, Search, Trash2 } from 'lucide-react';
import { apiClient, ApiError } from '@/lib/api';
import { parseCallsFiltersFromSearch } from '@/lib/calls-filters';
import { resolveCustomerNameDisplay } from '@/lib/customer-name';
import { isAdminRole, useAuthStore } from '@/lib/auth-store';
import type {
  CallStatus,
  CallRecordSummary,
  PaginatedCallRecords,
} from '@/lib/types';
import { Card } from '@/components/ui/card';
import { RevealPhoneCell } from '@/components/calls/reveal-phone';
import { CallStatusBadge, QcResultBadge } from '@/components/ui/status-badge';
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from '@/components/ui/empty-state';
import { formatDateTime, formatDuration, formatScore } from '@/lib/format';
import { exportCallsCsv, toCallsExportQuery } from '@/lib/export-api';
import { deleteCallApi } from '@/lib/calls-api';
import { DeleteWithCaptchaModal } from '@/components/ui/delete-captcha';

const STATUS_OPTIONS: Array<{ value: CallStatus | ''; label: string }> = [
  { value: '', label: '全部状态' },
  { value: 'PENDING', label: '待处理' },
  { value: 'PROCESSING', label: '处理中' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'FAILED', label: '失败' },
];

export function CallsPage() {
  const [params, setParams] = useSearchParams();
  // Issue #228：仅 ADMIN / SUPER_ADMIN 可对完整手机号发起二次密码揭示；MANAGER 仅见脱敏。
  const user = useAuthStore((s) => s.user);
  const canRevealPhone = isAdminRole(user?.role);
  // Issue #8：通话记录删除仅 ADMIN / SUPER_ADMIN（后端 @Roles(ADMIN) + service 双重校验，
  // 这里只是不给无权用户渲染入口，权限判定绝不依赖前端）。
  const canDeleteCall = isAdminRole(user?.role);

  const filters = parseCallsFiltersFromSearch(`?${params.toString()}`);
  const limit = filters.limit ?? 20;

  // Issue #8：导出 / 删除的 UI 状态。
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CallRecordSummary | null>(null);

  const [pendingPhone, setPendingPhone] = useState(filters.phoneNumber ?? '');
  const [pendingCustomer, setPendingCustomer] = useState(filters.customerName ?? '');
  const [pendingStatus, setPendingStatus] = useState<CallStatus | ''>(
    filters.status ?? '',
  );
  const [pendingMinScore, setPendingMinScore] = useState(
    filters.minScore?.toString() ?? '',
  );
  const [pendingMaxScore, setPendingMaxScore] = useState(
    filters.maxScore?.toString() ?? '',
  );
  const [pendingFrom, setPendingFrom] = useState(filters.startedAfter ?? '');
  const [pendingTo, setPendingTo] = useState(filters.startedBefore ?? '');

  const query = useQuery({
    queryKey: ['calls', filters],
    queryFn: () =>
      apiClient.get<PaginatedCallRecords>(
        '/calls',
        filters as unknown as Record<string, unknown>,
      ),
    placeholderData: keepPreviousData,
  });

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams();
    if (pendingStatus) next.set('status', pendingStatus);
    if (pendingMinScore) next.set('minScore', pendingMinScore);
    if (pendingMaxScore) next.set('maxScore', pendingMaxScore);
    if (pendingFrom) next.set('startedAfter', pendingFrom);
    if (pendingTo) next.set('startedBefore', pendingTo);
    if (pendingPhone.trim()) next.set('phoneNumber', pendingPhone.trim());
    if (pendingCustomer.trim()) next.set('customerName', pendingCustomer.trim());
    next.set('page', '1');
    next.set('limit', String(limit));
    setParams(next, { replace: true });
  }

  function resetFilters() {
    setPendingPhone('');
    setPendingCustomer('');
    setPendingStatus('');
    setPendingMinScore('');
    setPendingMaxScore('');
    setPendingFrom('');
    setPendingTo('');
    setParams(new URLSearchParams(), { replace: true });
  }

  function gotoPage(nextPage: number) {
    const next = new URLSearchParams(params);
    next.set('page', String(nextPage));
    setParams(next, { replace: true });
  }

  /**
   * Issue #8：导出**当前筛选条件下的全部记录**（不是当前页）。
   *
   * 传给导出接口的就是列表正在用的那份 `filters`，只剔除 page / limit —— 导出范围因此
   * 天然与页面上看到的筛选一致；后端同样忽略分页参数（双保险）。
   * 走真实后端接口 + Blob 下载；失败（403 / 413 / 非预期 content-type）会抛 ApiError，
   * 这里把后端中文 message 展示出来，绝不静默失败、绝不用当前页 state 伪造文件。
   */
  async function exportCurrentFilters() {
    setExporting(true);
    setExportError(null);
    try {
      await exportCallsCsv(
        toCallsExportQuery(filters as unknown as Record<string, unknown>),
      );
    } catch (err) {
      setExportError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : '导出失败，请稍后重试。',
      );
    } finally {
      setExporting(false);
    }
  }

  const pagination = query.data?.pagination;
  const items = query.data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs text-slate-500">通话管理 / 通话记录</div>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">通话记录</h1>
          <p className="mt-1 text-sm text-slate-500">
            手机号始终脱敏展示。客户姓名仅对 ADMIN / SUPER_ADMIN 显示完整名，其余角色为脱敏视图。
          </p>
        </div>
        {/* Issue #8：导出当前筛选条件下的全部记录（非仅当前页）。 */}
        <div className="flex flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => void exportCurrentFilters()}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
            data-testid="calls-export"
            title="导出当前筛选条件下的全部通话记录（CSV，非仅当前页）"
          >
            <Download className="h-4 w-4" />
            {exporting ? '导出中…' : '导出'}
          </button>
          <span className="text-xs text-slate-400">导出当前筛选全部记录</span>
          {exportError ? (
            <span
              className="max-w-xs text-right text-xs text-rose-600"
              data-testid="calls-export-error"
            >
              {exportError}
            </span>
          ) : null}
        </div>
      </header>

      <Card title="筛选条件">
        <form
          className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6"
          onSubmit={applyFilters}
          data-testid="calls-filter"
        >
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">脱敏号码</span>
            <input
              type="text"
              value={pendingPhone}
              onChange={(e) => setPendingPhone(e.target.value)}
              placeholder="如 13900001111"
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              data-testid="filter-phone"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">客户姓名</span>
            <input
              type="text"
              value={pendingCustomer}
              onChange={(e) => setPendingCustomer(e.target.value)}
              placeholder="精确匹配（后端按哈希查询）"
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">通话状态</span>
            <select
              value={pendingStatus}
              onChange={(e) => setPendingStatus(e.target.value as CallStatus | '')}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
              data-testid="filter-status"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">最低分</span>
            <input
              type="number"
              min={0}
              max={100}
              value={pendingMinScore}
              onChange={(e) => setPendingMinScore(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">最高分</span>
            <input
              type="number"
              min={0}
              max={100}
              value={pendingMaxScore}
              onChange={(e) => setPendingMaxScore(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">起始时间</span>
            <input
              type="datetime-local"
              value={pendingFrom}
              onChange={(e) => setPendingFrom(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">截止时间</span>
            <input
              type="datetime-local"
              value={pendingTo}
              onChange={(e) => setPendingTo(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
            />
          </label>
          <div className="flex items-end gap-2 md:col-span-3 xl:col-span-2">
            <button
              type="submit"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
            >
              <Search className="h-4 w-4" /> 查询
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
            >
              重置
            </button>
          </div>
        </form>
      </Card>

      <Card
        title="通话列表"
        description={
          pagination
            ? `共 ${pagination.total} 条 · 第 ${pagination.page}/${pagination.totalPages} 页`
            : undefined
        }
      >
        {query.isLoading ? (
          <LoadingState label="正在加载通话列表…" />
        ) : query.isError ? (
          <ErrorState
            title="通话列表加载失败"
            description={query.error instanceof Error ? query.error.message : '请稍后重试'}
            retry={() => query.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title="没有符合条件的通话"
            description="尝试放宽筛选条件，或确认所选日期范围内确有通话上传。"
          />
        ) : (
          <div className="overflow-x-auto" data-testid="calls-table">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="text-xs text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">客户</th>
                  {/* Issue #228：新增手机号（脱敏 + 二次密码揭示）、部门、岗位列。 */}
                  <th className="px-3 py-2 text-left font-medium">手机号</th>
                  <th className="px-3 py-2 text-left font-medium">坐席</th>
                  <th className="px-3 py-2 text-left font-medium">部门</th>
                  <th className="px-3 py-2 text-left font-medium">岗位</th>
                  <th className="px-3 py-2 text-left font-medium">时长</th>
                  <th className="px-3 py-2 text-left font-medium">开始时间</th>
                  <th className="px-3 py-2 text-left font-medium">AI 分</th>
                  <th className="px-3 py-2 text-left font-medium">通话状态</th>
                  <th className="px-3 py-2 text-left font-medium">质检</th>
                  <th className="px-3 py-2 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">
                        {resolveCustomerNameDisplay(row)}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {canRevealPhone ? (
                        <RevealPhoneCell callId={row.id} phoneMasked={row.phoneMasked} />
                      ) : (
                        <span className="text-xs text-slate-500">{row.phoneMasked || '—'}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{row.employeeName ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{row.employeeDeptName ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-500">{row.employeePosition ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {formatDuration(row.duration)}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {formatDateTime(row.startedAt)}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {formatScore(row.qcScore ?? row.aiPredictedScore)}
                    </td>
                    <td className="px-3 py-2">
                      <CallStatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-2">
                      <QcResultBadge result={row.qcResult} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/calls/${row.id}`}
                          className="text-xs font-medium text-brand-600 hover:underline"
                        >
                          查看详情
                        </Link>
                        {/* Issue #8：软删除入口（仅 ADMIN+；后端仍会二次校验角色）。 */}
                        {canDeleteCall ? (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(row)}
                            className="inline-flex items-center gap-1 text-xs font-medium text-rose-600 hover:underline"
                            data-testid={`call-delete-${row.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            删除
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.totalPages > 1 ? (
          <div className="mt-4 flex items-center justify-end gap-2 text-sm">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 disabled:opacity-50"
              disabled={pagination.page <= 1}
              onClick={() => gotoPage(pagination.page - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> 上一页
            </button>
            <span className="text-xs text-slate-500">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs text-slate-600 disabled:opacity-50"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => gotoPage(pagination.page + 1)}
            >
              下一页 <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </Card>

      {/*
        Issue #8：删除二次确认 —— 复用本仓既有的「删除 + 一次性图形验证码」弹窗
        （与员工 / 课程 / 知识库删除同一模式）。onConfirm 调真实后端 API；成功后
        refetch 列表（绝不本地删行伪造已删除状态）。key 绑定目标 id，保证每次打开都重挂载、
        验证码与输入框都是干净的。
      */}
      {deleteTarget ? (
        <DeleteWithCaptchaModal
          key={deleteTarget.id}
          action="call:delete"
          title="删除通话记录"
          description="删除后该记录将从列表、详情、统计与导出中不可见。录音文件与质检报告仍会保留，删除操作会写入审计日志。"
          body={
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <div>
                客户：
                <span className="font-medium text-slate-900">
                  {resolveCustomerNameDisplay(deleteTarget)}
                </span>
              </div>
              <div className="mt-1">
                手机号：<span className="font-medium">{deleteTarget.phoneMasked || '—'}</span>
              </div>
              <div className="mt-1">
                坐席：{deleteTarget.employeeName ?? '—'} · 开始时间：
                {formatDateTime(deleteTarget.startedAt)}
              </div>
            </div>
          }
          onClose={() => setDeleteTarget(null)}
          onConfirm={async (proof) => {
            await deleteCallApi(deleteTarget.id, proof);
            // 真实后端删除成功后再刷新列表，保证 UI 与后端一致。
            await query.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
